import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Grid,
  LinearProgress,
  TextField,
  Tabs,
  Tab,
  Chip,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DownloadIcon from '@mui/icons-material/Download'
import InfoIcon from '@mui/icons-material/Info'
import HistoryIcon from '@mui/icons-material/History'
import { posStorePromotionService } from '../services'
import { getUploadSuccessMessage } from '../utils/uploadSuccessMessages'
import UploadErrorDetailsDialog from '../components/UploadErrorDetailsDialog'
import UploadHistoryRefreshButton from '../components/UploadHistoryRefreshButton'
import UploadHistoryTableWrapper from '../components/UploadHistoryTableWrapper'
import UploadImportStatusAlert from '../components/UploadImportStatusAlert'
import InstructionCard from '../components/common/InstructionCard'
import { promotionUploadInstructions } from '../constants/uploadInstructions'
import { useUploadHistoryPolling } from '../hooks/useUploadHistoryPolling'
import { upcomingPromotionCsvDateError } from '../utils/upcomingPromotionCsv'
import { pollUploadLogUntilSettled } from '../utils/pollUploadLogUntilSettled'

function uploadAuthorLabel(log) {
  const name = String(log?.admin_name || '')
    .replace(/\bundefined\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
  const email = String(log?.admin_email || '').trim()
  if (name && email && name.toLowerCase() !== email.toLowerCase()) {
    return { primary: name, secondary: email }
  }
  return { primary: name || email || '—', secondary: '' }
}

const TabPanel = (props) => {
  const { children, value, index, ...other } = props
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

const PosStorePromotionsUploadPage = () => {
  const [file, setFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [totalItems, setTotalItems] = useState(0)
  const [processedItems, setProcessedItems] = useState(0)
  const [batchSize, setBatchSize] = useState(10)
  const [uploadLogs, setUploadLogs] = useState([])
  const [logsLoading, setLogsLoading] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [errorDetailsDialog, setErrorDetailsDialog] = useState({ open: false, uploadLog: null })
  const [logsPage, setLogsPage] = useState(0)
  const [logsRowsPerPage, setLogsRowsPerPage] = useState(10)
  const [logsTotalCount, setLogsTotalCount] = useState(0)
  const [isDragOver, setIsDragOver] = useState(false)
  const logsFetchInFlightRef = useRef(false)

  const handleFileChange = (e) => {
    setFile(e.target.files[0])
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
    
    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      const droppedFile = files[0]
      if (droppedFile.type === 'text/csv' || droppedFile.name.endsWith('.csv')) {
        setFile(droppedFile)
      } else {
        setMessage({ type: 'error', text: 'Please upload a CSV file only' })
      }
    }
  }

  const handleUpload = async () => {
    if (!file) {
      setMessage({ type: 'error', text: 'Please select a file to upload' })
      return
    }

    try {
      setLoading(true)
      setUploadProgress(0)
      setProcessedItems(0)
      
      const text = await file.text()
      const dateError = upcomingPromotionCsvDateError(text)
      if (dateError) {
        setMessage({ type: 'error', text: dateError })
        return
      }
      const lines = text.split('\n').filter(line => line.trim())
      const itemCount = Math.max(0, lines.length - 1)
      setTotalItems(itemCount)
      
      const progressInterval = setInterval(() => {
        setProcessedItems(prev => {
          const next = Math.min(prev + Math.floor(itemCount * 0.1), itemCount - 1)
          setUploadProgress(Math.round((next / itemCount) * 80))
          return next
        })
      }, 300)
      
      const result = await posStorePromotionService.importPromotions({
        file,
        batchSize: batchSize.toString(),
      })
      
      clearInterval(progressInterval)
      setProcessedItems(itemCount)
      setUploadProgress(100)
      
      if (result.success && result.message) {
        setMessage({
          type: 'success',
          text: result.message || getUploadSuccessMessage('upcomingPromotion'),
          showHistoryLink: true,
        })
        setFile(null)
        fetchUploadLogs()

        if (result.logId) {
          try {
            const uploadLog = await pollUploadLogUntilSettled(
              (logId) => posStorePromotionService.getUploadLogDetails(logId),
              result.logId,
            )
            setErrorDetailsDialog({ open: true, uploadLog })
            fetchUploadLogs({ silent: true })
          } catch (pollError) {
            console.error('Upload result polling:', pollError)
            setMessage({
              type: 'warning',
              text:
                pollError?.message ||
                'Import started. Check Upload History for results.',
              showHistoryLink: true,
            })
          }
        }

        setTimeout(() => {
          setUploadProgress(0)
          setProcessedItems(0)
          setTotalItems(0)
        }, 2000)
      }
    } catch (error) {
      const data = error.response?.data
      const errorMessage =
        (typeof data?.message === 'string' && data.message) ||
        (Array.isArray(data?.message) ? data.message.join('; ') : null) ||
        'Error uploading POS store promotions'
      setMessage({
        type: 'error',
        text: errorMessage,
      })
      const logId = data?.logId
      const inlineErrors = Array.isArray(data?.error_details) ? data.error_details : []
      try {
        if (logId) {
          const response = await posStorePromotionService.getUploadLogDetails(logId)
          setErrorDetailsDialog({ open: true, uploadLog: response.data || response })
        } else if (inlineErrors.length > 0) {
          setErrorDetailsDialog({
            open: true,
            uploadLog: {
              file_name: file?.name || 'upload.csv',
              status: 'failed',
              failed_imports: inlineErrors.length,
              total_rows: inlineErrors.length,
              successful_imports: 0,
              error_details: inlineErrors,
            },
          })
        }
      } catch (detailsError) {
        console.error('Error loading upload error details:', detailsError)
      }
      setUploadProgress(0)
      setProcessedItems(0)
      setTotalItems(0)
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadUploadFile = async (log) => {
    try {
      const response = await posStorePromotionService.downloadUploadFile(log.id)
      const blob = response.data
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = log.file_name || 'upcoming-promotions.csv'
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading upcoming promotion file:', error)
      setMessage({
        type: 'error',
        text: 'Could not download the stored upload file',
      })
    }
  }

  const handleViewErrorDetails = async (logId) => {
    try {
      const response = await posStorePromotionService.getUploadLogDetails(logId)
      setErrorDetailsDialog({ open: true, uploadLog: response.data || response })
    } catch (error) {
      console.error('Error fetching upload log details:', error)
    }
  }

  const fetchUploadLogs = useCallback(async ({ silent = false } = {}) => {
    if (logsFetchInFlightRef.current) {
      return
    }
    logsFetchInFlightRef.current = true
    try {
      if (!silent) {
        setLogsLoading(true)
      }
      const response = await posStorePromotionService.getUploadLogs({
        page: logsPage + 1,
        limit: logsRowsPerPage
      })
      
      const logsData = response.items || response.data || response.logs || (Array.isArray(response) ? response : [])
      const totalCount = response.pagination?.total || response.totalCount || response.total || (Array.isArray(logsData) ? logsData.length : 0)
      
      setUploadLogs(Array.isArray(logsData) ? logsData : [])
      setLogsTotalCount(totalCount)
    } catch (error) {
      console.error('Error fetching upload logs:', error)
      if (!silent) {
        setUploadLogs([])
        setLogsTotalCount(0)
      }
    } finally {
      if (!silent) {
        setLogsLoading(false)
      }
      logsFetchInFlightRef.current = false
    }
  }, [logsPage, logsRowsPerPage])

  const handleLogsPageChange = (event, newPage) => {
    setLogsPage(newPage)
  }

  const handleLogsRowsPerPageChange = (event) => {
    setLogsRowsPerPage(parseInt(event.target.value, 10))
    setLogsPage(0)
  }

  const handleDownloadSampleCSV = async () => {
    try {
      const blob = await posStorePromotionService.downloadSampleCsv()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.setAttribute('href', url)
      link.setAttribute('download', 'upcoming-promotions-sample.csv')
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to download sample CSV',
      })
    }
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  const handleViewUploadHistory = useCallback(() => {
    setActiveTab(1)
    fetchUploadLogs()
  }, [fetchUploadLogs])

  useEffect(() => {
    if (activeTab === 1) {
      fetchUploadLogs()
    }
  }, [activeTab, fetchUploadLogs])

  useUploadHistoryPolling(activeTab === 1, fetchUploadLogs)

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  return (
    <>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 700, color: '#1a1a1a' }}>
          Upload Upcoming Promotions
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
          Import upcoming promotions from CSV. Dates must be yyyy-mm-dd (for example 2026-08-01). The same article_code can appear on multiple rows when windows do not overlap; overlapping windows for one article in the same file are rejected. A results summary opens when processing completes.
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Upload Upcoming Promotions" icon={<CloudUploadIcon />} iconPosition="start" />
            <Tab label="Upload History" icon={<HistoryIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* Upload Upcoming Promotions Tab */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <CardContent>
                  {message && (
                    <UploadImportStatusAlert
                      severity={message.type}
                      onClose={() => setMessage(null)}
                      showHistoryLink={message.showHistoryLink}
                      onViewUploadHistory={handleViewUploadHistory}
                    >
                      {message.text}
                    </UploadImportStatusAlert>
                  )}

                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CloudUploadIcon sx={{ color: '#e90000' }} />
                    Upload Upcoming Promotions CSV
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        border: isDragOver ? '2px solid #1B5E20' : '2px dashed #e90000',
                        borderRadius: 2,
                        p: 4,
                        textAlign: 'center',
                        cursor: 'pointer',
                        bgcolor: isDragOver ? '#E8F5E9' : '#f8f9fb',
                        transition: 'all 0.3s ease',
                        '&:hover': { 
                          bgcolor: '#E8F5E9',
                          borderColor: '#1B5E20',
                        },
                      }}
                      onClick={() => document.getElementById('file-input').click()}
                      onDragOver={handleDragOver}
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <CloudUploadIcon sx={{ fontSize: 56, color: '#e90000', mb: 2 }} />
                      <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                        {file ? file.name : isDragOver ? 'Drop CSV file here' : 'Click to upload or drag and drop'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        CSV file format (Max 10MB)
                      </Typography>
                      <input
                        id="file-input"
                        type="file"
                        accept=".csv"
                        onChange={handleFileChange}
                        hidden
                      />
                    </Box>
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <TextField
                      label="Batch Size"
                      type="number"
                      value={batchSize}
                      onChange={(e) => setBatchSize(Math.max(1, parseInt(e.target.value, 10) || 10))}
                      fullWidth
                      size="small"
                      helperText="Number of rows to process per batch (default: 10)"
                      disabled={loading}
                    />
                  </Box>

                  {loading && uploadProgress > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                          Upload Progress
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          {uploadProgress}% ({processedItems}/{totalItems} items)
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={uploadProgress}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            backgroundColor: '#e90000',
                          },
                        }}
                      />
                    </Box>
                  )}

                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handleUpload}
                    disabled={loading || !file}
                    sx={{
                      bgcolor: '#e90000',
                      py: 1.5,
                      fontWeight: 600,
                      '&:hover': { bgcolor: '#cc0000' },
                    }}
                  >
                    {loading ? <CircularProgress size={24} sx={{ mr: 1 }} /> : null}
                    {loading ? 'Uploading...' : 'Upload Upcoming Promotions'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                    <DownloadIcon sx={{ color: '#e90000', fontSize: 40 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Sample CSV
                    </Typography>
                  </Box>

                  <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                    Download a sample CSV to see the required format.
                  </Typography>

                  <Button
                    variant="outlined"
                    fullWidth
                    size="large"
                    startIcon={<DownloadIcon sx={{ fontSize: 20 }} />}
                    onClick={handleDownloadSampleCSV}
                    sx={{
                      color: '#e90000',
                      borderColor: '#e90000',
                      fontWeight: 600,
                      '&:hover': { bgcolor: '#E8F5E9' },
                      py: 1.2,
                    }}
                  >
                    Download Sample
                  </Button>
                </CardContent>
              </Card>

              <InstructionCard
                {...promotionUploadInstructions}
                icon={<InfoIcon />}
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Upload History Tab */}
        <TabPanel value={activeTab} index={1}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Upload History
                </Typography>
                <UploadHistoryRefreshButton
                  onClick={fetchUploadLogs}
                  loading={logsLoading}
                />
              </Box>

              <UploadHistoryTableWrapper
                loading={logsLoading}
                isEmpty={uploadLogs.length === 0}
              >
                <>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                          <TableCell sx={{ fontWeight: 600 }}>File Name</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Uploaded by</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Uploaded at</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Superseded</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">Total</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">Success</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">Failed</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">Success Rate</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {uploadLogs.map((log) => {
                          const author = uploadAuthorLabel(log)
                          return (
                          <TableRow key={log.id} sx={{ '&:hover': { bgcolor: '#f8f9fb' } }}>
                            <TableCell>{log.file_name}</TableCell>
                            <TableCell>
                              <Box>
                                <Typography variant="body2" sx={{ color: '#25314C' }}>
                                  {author.primary}
                                </Typography>
                                {author.secondary ? (
                                  <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                    {author.secondary}
                                  </Typography>
                                ) : null}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ color: '#666', whiteSpace: 'nowrap' }}>
                              {formatDate(log.created_at)}
                            </TableCell>
                            <TableCell>
                              {log.superseded === 'full' || log.superseded === true ? (
                                <Chip
                                  label="Superseded"
                                  size="small"
                                  sx={{
                                    bgcolor: '#f3f4f6',
                                    color: '#374151',
                                    fontWeight: 600,
                                  }}
                                />
                              ) : log.superseded === 'partial' ? (
                                <Chip
                                  label="Partial"
                                  size="small"
                                  sx={{
                                    bgcolor: '#fff7ed',
                                    color: '#c2410c',
                                    fontWeight: 600,
                                  }}
                                />
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell align="center">{log.total_rows}</TableCell>
                            <TableCell align="center" sx={{ color: '#4caf50', fontWeight: 600 }}>
                              {log.successful_imports}
                            </TableCell>
                            <TableCell align="center" sx={{ color: '#f44336', fontWeight: 600 }}>
                              {log.failed_imports}
                            </TableCell>
                            <TableCell align="center">
                              {log.total_rows > 0 
                                ? `${Math.round((log.successful_imports / log.total_rows) * 100)}%` 
                                : '0%'
                              }
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={log.status}
                                size="small"
                                sx={{
                                  bgcolor: log.status === 'completed' ? '#e8f5e9' : log.status === 'processing' ? '#fff3e0' : '#ffebee',
                                  color: log.status === 'completed' ? '#1b5e20' : log.status === 'processing' ? '#e65100' : '#c62828',
                                  fontWeight: 600,
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                {log.s3_key ? (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleDownloadUploadFile(log)}
                                  >
                                    Download
                                  </Button>
                                ) : null}
                                {log.failed_imports > 0 && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    onClick={() => handleViewErrorDetails(log.id)}
                                  >
                                    View Errors
                                  </Button>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={logsTotalCount}
                    page={logsPage}
                    onPageChange={handleLogsPageChange}
                    rowsPerPage={logsRowsPerPage}
                    onRowsPerPageChange={handleLogsRowsPerPageChange}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    showFirstButton
                    showLastButton
                  />
                </>
              </UploadHistoryTableWrapper>
            </CardContent>
          </Card>
        </TabPanel>
      </Container>

      <UploadErrorDetailsDialog 
        open={errorDetailsDialog.open}
        onClose={() => setErrorDetailsDialog({ open: false, uploadLog: null })}
        uploadLog={errorDetailsDialog.uploadLog}
      />
    </>
  )
}

export default PosStorePromotionsUploadPage
